import {useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {isEmpty} from 'lodash';
import UberEatsRepository, {
  UberStoreMapping,
} from '../../../internals/repository/UberEatsRepository';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Uber Eats integration settings for the selected shop.
 *
 * Maps the merchant's Uber store UUID to this shop: once saved + enabled,
 * incoming Uber Eats orders for that store are attributed to this shop and
 * appear in its POS order queue.
 */
const UberEatsInfo = (props: any) => {
  const {selectedShop} = props;

  const [mapping, setMapping] = useState<UberStoreMapping | null>(null);
  const [uberStoreId, setUberStoreId] = useState<string>('');
  const [enabled, setEnabled] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [publishing, setPublishing] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const loadMapping = useCallback(async () => {
    if (!selectedShop?.shopId) return;
    setLoading(true);
    setError('');
    try {
      const mappings = await UberEatsRepository.getMapping(selectedShop.shopId);
      const current = mappings[0] ?? null;
      setMapping(current);
      setUberStoreId(current?.uberStoreId ?? '');
      setEnabled(current ? current.enabled : true);
    } catch (e: any) {
      setError(e.message || 'Failed to load the Uber Eats configuration.');
    } finally {
      setLoading(false);
    }
  }, [selectedShop?.shopId]);

  useEffect(() => {
    loadMapping();
  }, [loadMapping]);

  const onSave = async () => {
    setError('');
    setSuccess('');
    const trimmed = uberStoreId.trim();
    if (!UUID_RE.test(trimmed)) {
      setError(
        'The Uber Store ID must be a UUID, e.g. a2f90627-bf07-4f8f-93db-b171705c9bda. ' +
          'Find it in Uber Eats Manager → Settings, or ask Netlise support.'
      );
      return;
    }
    setSaving(true);
    try {
      const saved = await UberEatsRepository.saveMapping(
        selectedShop.shopId,
        trimmed,
        enabled
      );
      setMapping(saved);
      setSuccess(
        saved.enabled
          ? 'Uber Eats store connected. Incoming orders will appear in this shop’s POS queue.'
          : 'Mapping saved. The integration is currently disabled.'
      );
    } catch (e: any) {
      setError(e.message || 'Failed to save the Uber Eats configuration.');
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async () => {
    if (!mapping) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await UberEatsRepository.deleteMapping(mapping.uberStoreId);
      setMapping(null);
      setUberStoreId('');
      setEnabled(true);
      setSuccess('Uber Eats store disconnected from this shop.');
    } catch (e: any) {
      setError(e.message || 'Failed to remove the mapping.');
    } finally {
      setSaving(false);
    }
  };

  const provisionBadge = () => {
    if (!mapping) return null;
    const status = mapping.provisionStatus ?? 'not_provisioned';
    const ok = status === 'provisioned';
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
          ok ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
        }`}
      >
        {ok
          ? 'Provisioned with Uber'
          : 'Saved, Uber-side activation pending'}
      </span>
    );
  };

  if (isEmpty(selectedShop)) {
    return (
      <div className="h-full w-full p-6 text-sm text-gray-500">
        Select a shop to configure Uber Eats.
      </div>
    );
  }

  return (
    <div className="h-full w-full p-6">
      <div className="h-full w-full max-w-screen-md bg-white shadow-md rounded-lg">
        <div className="flex flex-row justify-between items-center bg-gray-50 px-4 py-3 sm:px-6 rounded-t-md">
          <h3>{'Uber Eats Integaration'}</h3>
          {provisionBadge()}
        </div>

        <div className="bg-white px-4 py-5 sm:p-6 space-y-6">
          <p className="text-sm text-gray-500">
            Connect this shop ({selectedShop.shopName}) to its Uber Eats store.
            Once enabled, orders placed on Uber Eats are delivered straight to
            the shop&apos;s POS order queue for accept / deny / preparation.
            Use <span className="font-medium">Connect with Uber</span> to
            authorize the store, Uber requires the store owner&apos;s consent
            to activate a POS integration. The mapping below is created
            automatically after authorization; manual Save is a fallback.
          </p>

          {loading ? (
            <div className="text-sm text-gray-400">Loading configuration…</div>
          ) : (
            <>
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-4">
                  <label
                    htmlFor="uberStoreId"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Uber Store ID
                  </label>
                  <input
                    id="uberStoreId"
                    type="text"
                    value={uberStoreId}
                    onChange={(e) => setUberStoreId(e.target.value)}
                    placeholder="e.g. a2f90627-bf07-4f8f-93db-b171705c9bda"
                    className="h-10 block w-full rounded-md outline-none border-gray-300 border shadow px-3 sm:text-sm font-mono"
                  />
                  <span className="mt-1 block text-xs text-gray-400">
                    The store UUID from Uber Eats Manager (Settings → Store
                    info) or the Uber developer portal.
                  </span>
                </div>

                <div className="col-span-6 sm:col-span-2 flex items-center pt-6">
                  <label className="flex items-center text-sm font-medium text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                      className="mr-3 w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    Enable order flow
                  </label>
                </div>
              </div>

              {mapping && (
                <div className="text-xs text-gray-400">
                  Last updated:{' '}
                  {mapping.updatedAt
                    ? new Date(mapping.updatedAt).toLocaleString()
                    : '—'}
                </div>
              )}

              {error && (
                <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}
              {success && (
                <div className="rounded-md bg-green-50 px-4 py-3 text-sm text-green-700">
                  {success}
                </div>
              )}

              <div className="flex flex-row items-center space-x-3">
                <button
                  type="button"
                  disabled={saving}
                  onClick={async () => {
                    // Open the tab synchronously (popup blockers), then point
                    // it at the server-generated, HMAC-signed consent URL.
                    const tab = window.open('about:blank', '_blank');
                    try {
                      const url = await UberEatsRepository.getConnectUrl(
                        selectedShop.shopId
                      );
                      if (tab) tab.location.href = url;
                      else window.location.href = url;
                    } catch (e: any) {
                      tab?.close();
                      setError(e.message || 'Could not start the Uber connection.');
                    }
                  }}
                  className="inline-flex justify-center rounded-md border border-transparent bg-black py-2 px-6 text-sm font-medium text-white shadow hover:bg-gray-800 focus:outline-none disabled:opacity-50"
                >
                  Connect with Uber
                </button>
                <button
                  type="button"
                  disabled={saving || !uberStoreId.trim()}
                  onClick={onSave}
                  className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-12 text-sm font-medium text-white shadow hover:bg-indigo-700 focus:outline-none disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                {mapping && (
                  <button
                    type="button"
                    disabled={publishing || saving}
                    onClick={async () => {
                      setError('');
                      setSuccess('');
                      setPublishing(true);
                      try {
                        const res = await UberEatsRepository.publishMenu(
                          selectedShop.shopId
                        );
                        const skippedNote = res.skipped?.length
                          ? ` (${res.skipped.length} skipped — hidden or unpriced)`
                          : '';
                        setSuccess(
                          `Menu published to Uber Eats: ${res.itemsPublished} item(s)${skippedNote}. ` +
                            'New Uber orders will now match POS products exactly.'
                        );
                      } catch (e: any) {
                        setError(e.message || 'Menu publish failed.');
                      } finally {
                        setPublishing(false);
                      }
                    }}
                    className="inline-flex justify-center rounded-md border border-transparent bg-emerald-600 py-2 px-6 text-sm font-medium text-white shadow hover:bg-emerald-700 focus:outline-none disabled:opacity-50"
                  >
                    {publishing ? 'Publishing…' : 'Publish Menu to Uber'}
                  </button>
                )}
                {mapping && (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={onRemove}
                    className="inline-flex justify-center rounded-md border border-transparent bg-light-coral py-2 px-6 text-sm font-medium text-white shadow focus:outline-none disabled:opacity-50"
                  >
                    Disconnect
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        <div className="bg-gray-50 h-12 text-right sm:px-6 rounded-b-md" />
      </div>
    </div>
  );
};

export default connect(
  (state: any) => ({
    selectedShop: state.dashboard.get('selectedShop'),
  }),
  {}
)(UberEatsInfo);
